import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import toast from 'react-hot-toast'
import {
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
} from 'react-icons/hi'
import ConfirmModal from '../../components/ConfirmModal'
import useDocumentTitle from '../../hooks/useDocumentTitle'

const PAGE_SIZE = 10

export default function AgenciesPage() {
  useDocumentTitle('Agencies')

  const [agencies, setAgencies] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [confirmModal, setConfirmModal] = useState({ open: false })

  const fetchAgencies = useCallback(async () => {
    setLoading(true)
    try {
      let query = supabase
        .from('agencies')
        .select('id, name, access_code, created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

      if (search.trim()) {
        query = query.ilike('name', `%${search.trim()}%`)
      }

      const { data, error, count } = await query

      if (error) throw error
      const agenciesData = data || []

      if (agenciesData.length === 0) {
        setAgencies([])
        setTotalCount(count || 0)
        return
      }

      const agencyNames = agenciesData
        .map((agency) => agency.name)
        .filter(Boolean)

      const { data: requestData, error: requestError } = await supabase
        .from('agency_requests')
        .select('agency_name, agency_email, sector_id, created_at')
        .in('agency_name', agencyNames)
        .order('created_at', { ascending: false })

      if (requestError) throw requestError

      const requestMap = new Map()

      ;(requestData || []).forEach((request) => {
        if (!requestMap.has(request.agency_name)) {
          requestMap.set(request.agency_name, request)
        }
      })

      const mergedAgencies = agenciesData.map((agency) => {
        const request = requestMap.get(agency.name)

        return {
          ...agency,
          request_email: request?.agency_email || '',
          request_sector: request?.sector_id || '',
        }
      })

      setAgencies(mergedAgencies)
      setTotalCount(count || 0)
    } catch (err) {
      toast.error('Failed to load agencies')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => {
    fetchAgencies()
  }, [fetchAgencies])

  useEffect(() => {
    setPage(0)
  }, [search])

  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const handleDelete = (agency) => {
    setConfirmModal({
      open: true,
      title: 'Delete Agency',
      message: `Are you sure you want to permanently delete "${agency.name}"? This action cannot be undone.`,
      variant: 'danger',
      onConfirm: async () => {
        try {
          const { error } = await supabase
            .from('agencies')
            .delete()
            .eq('id', agency.id)

          if (error) throw error
          toast.success('Agency deleted successfully')
          fetchAgencies()
        } catch (err) {
          toast.error('Failed to delete agency')
        }
        setConfirmModal({ open: false })
      },
    })
  }

  return (
    <div className="agencies-page">
      <div className="page-header">
        <div>
          <h1>Agencies</h1>
          <p>Manage real estate agencies and brokerage offices</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <HiOutlineSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by agency name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="search-count">
          {totalCount} agenc{totalCount === 1 ? 'y' : 'ies'}
        </span>
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="table-loading">
            <div className="loading-spinner" />
            <p>Loading agencies...</p>
          </div>
        ) : agencies.length === 0 ? (
          <div className="table-empty">
            <HiOutlineSearch style={{ fontSize: '2rem', opacity: 0.4 }} />
            <p>No agencies found</p>
            {search && (
              <button
                className="btn btn-ghost"
                onClick={() => setSearch('')}
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Agency Name</th>
                <th>Access Code</th>
                <th>Email</th>
                <th>Sector</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agencies.map((agency) => (
                <tr key={agency.id}>
                  <td data-label="ID">
                    <span className="badge badge-default">#{agency.id}</span>
                  </td>
                  <td data-label="Agency">
                    <div className="cell-primary">
                      <strong>{agency.name}</strong>
                    </div>
                  </td>
                  <td data-label="Access Code">{agency.access_code || '-'}</td>
                  <td data-label="Email">{agency.request_email || '-'}</td>
                  <td data-label="Sector">{agency.request_sector || '-'}</td>
                  <td data-label="Created">
                    {new Date(agency.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td data-label="" className="td-actions">
                    <div className="table-actions">
                      <Link
                        to={`/agencies/${agency.id}/edit`}
                        className="btn btn-icon btn-ghost"
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </Link>
                      <button
                        className="btn btn-icon btn-ghost-danger"
                        title="Delete"
                        onClick={() => handleDelete(agency)}
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-ghost"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            <HiOutlineChevronLeft /> Previous
          </button>
          <span className="pagination-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn-ghost"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <HiOutlineChevronRight />
          </button>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ open: false })}
      />
    </div>
  )
}
